import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { EnvironmentConfig, BaseApiService, AuthenticationService, SearchModel, SearchResult, Storage } from "../../core/core";
import { AzureSearchService } from "../../azure-search/azure-search";
import { Activity } from "../models/activity.model";
import { map, Observable, tap } from "rxjs";

declare var environment: EnvironmentConfig;

@Injectable({ providedIn: "root" })
export class ActivityService extends BaseApiService<Activity> {
  private activities: Activity[] = [];

  // eventually just put this on the blob
  mappingsByActivityCode = {
    COI: {
      icon: "icon-share",
      route: "/renter/verification",
      title: "Upload Liability Insurance",
      description:
        "If you intend to transport the equipment you must provide a Certificate of Insurance to verify proof of general liability insurance coverage.<br />Policies must include automotive/cargo liability and have a minimum coverage of $1,000,000 per occurrence and $2,000,000 aggregate.<div class='mb-2'></div>Contact your commericial liability insurance company to add us as additional insured. Here is an <a target='_blank' href='https://company.rubbl.com/pubweb/marketplace/pdfs/example-coi.pdf'>example COI</a>.<div class='mb-2'></div> <b>Rubbl LLC<br/>1012 2nd St #100<br/>Encinitas, CA 92024</b>",
    },
    "TAX-EXEMPTION": {
      icon: "icon-share",
      route: "/renter/verification",
      description:
        "If you have a tax exemption certificate, upload it here. This is not required. <br /> If you do not upload a tax exemption certificate, taxes will be added to your rental invoices.",
    },
    "DRIVERS-LICENSE": {
      icon: "icon-share",
      route: "/renter/verification",
      description:
        "All renters must provide a picture of your Driver's License. This is used to verify your identity.<br />If you are a Rental Shop and wish to verify yourself a different way, please call us at <a href='tel:(866) 455-8531'>(866) 455-8531</a>",
    },
    SELFIE: {
      icon: "icon-share",
      route: "/renter/verification",
      fragment: "selfie",
      description:
        "All renters must provide a selfie. This is used to verify your identity.<br />If you are a Rental Shop and wish to verify yourself a different way, please call us at <a href='tel:(866) 455-8531'>(866) 455-8531</a>",
    },
    LISTMACHINE: { icon: "icon-stepper-add", route: "/owner/fleet/create" },
    "MACHINE-AVAILABILITY": {
      icon: "icon-statement",
      title: "Machine Availability Check",
      description: "Rubbl has a pending order for the following machine.",
      adminTitle: "Availability Check",
      adminDescription: "",
    },
    "JOB-VERIFICATION": {
      icon: "icon-insurance",
      adminTitle: "Job Verification",
      adminDescription: "Verify the jobsite.",
    },
    "RENTER-VERIFICATION": {
      icon: "icon-insurance",
      adminTitle: "Renter Verification",
      adminDescription: "COI, Drivers License, Selfie, Employment.",
    },
    "INSPECTION-OWNER-CHECKOUT": {
      icon: "icon-share",
      title: "Checkout Inspection",
      description:
        "Use your phone to complete an inspection. If you have bad cell service, you are able to upload photos/videos once you are back on wifi by coming back to this link.",
    },
    "INSPECTION-RENTER-CHECKIN": {
      icon: "icon-import",
      title: "Checkin Inspection",
      description:
        "Use your phone to complete an inspection. If you have bad cell service, you are able to upload photos/videos once you are back on wifi by coming back to this link.",
    },
    "INSPECTION-RENTER-CHECKOUT": {
      icon: "icon-share",
      title: "Checkout Inspection",
      description:
        "Use your phone to complete an inspection. If you have bad cell service, you are able to upload photos/videos once you are back on wifi by coming back to this link.",
    },
    "INSPECTION-OWNER-CHECKIN": {
      icon: "icon-import",
      title: "Checkin Inspection",
      description:
        "Use your phone to complete an inspection. If you have bad cell service, you are able to upload photos/videos once you are back on wifi by coming back to this link.",
    },
    "BUYITNOW-INSPECTION": {
      icon: "icon-statement",
      title: "Buy It Now Inspection",
      description:
        "Use your phone to complete an inspection. If you have bad cell service, you are able to upload photos/videos once you are back on wifi by coming back to this link.",
    },
  };

  constructor(httpClient: HttpClient, authenticationService: AuthenticationService, private azureSearchService: AzureSearchService) {
    super(httpClient, environment.adminApiDomain, "/api/admin/v1/activity", true, authenticationService);
  }

  getAllActivitiesByAccountId(accountId: string, activityContext: Activity["activityContext"] = "Initial"): Observable<Activity[]> {
    const searchModel = { ...new SearchModel(), activityContext, accountId, partitionKey: accountId, sortByField: "createdDateTime", sortAscending: false };
    return this.getAll(searchModel).pipe(
      map(result => {
        return this.setupActivities(result.results);
      }),
    );
  }

  getAllActivitiesByRentalRequestId(rentalRequestId: string, capabilityType?: Activity["capabilityType"]): Observable<Activity[]> {
    let searchModel = { ...new SearchModel(), rentalRequestId, sortByField: "createdDateTime", sortAscending: false };

    if (capabilityType) {
      searchModel["capabilityType"] = capabilityType;
    }

    return this.getAll(searchModel).pipe(
      map(result => {
        return this.setupActivities(result.results);
      }),
    );
  }

  getWithPartitionKey(id: string, partitionKey: string): Observable<Activity> {
    const headers = this.getAuth();
    return this.httpClient
      .get<any>(`${this.baseUrl}${this.endpoint}/${id}?partitionKey=${partitionKey}`, { headers })
      .pipe(
        map(result => {
          const activities = this.setupActivities([result as any]);
          return activities[0];
        }),
      ) as any;
  }

  // we are actually not getting initial activities and need to look into why. i assume this is getting called for manage layout in the manage resolver. and we wanted to load everyting
  getAllInitialActivities(accountId: string): Observable<SearchResult<Activity>> {
    // , activityContext: "Initial"
    const searchModel = { ...new SearchModel(), accountId, partitionKey: accountId, sortByField: "createdDateTime", sortAscending: false };
    return this.getAll(searchModel).pipe(
      tap(result => {
        this.activities = this.setupActivities(result.results);
      }),
    );
  }

  setupActivities(activities: Activity[]) {
    for (var activity of activities) {
      const mappingByActivityCode = this.mappingsByActivityCode[activity.activityCode];
      if (!mappingByActivityCode) {
        console.error("no mapping for activity. please create one");
        continue;
      }

      const title = environment.clientId == "rubbl.admin" ? mappingByActivityCode?.adminTitle : mappingByActivityCode?.title;
      const description = environment.clientId == "rubbl.admin" ? mappingByActivityCode?.adminDescription : mappingByActivityCode?.description;

      // check to see if we have a mapping by activity code first. if we dont then default to the mapping by activity type
      activity.icon = mappingByActivityCode.icon;
      activity.route = mappingByActivityCode?.route || `/activity/${activity.id}`;

      if (environment.clientId == "rubbl.admin" && activity.activityContext == "Order") {
        activity.route = `../activity/${activity.id}`;
        activity.queryParams = { partitionKey: activity.partitionKey };
      }

      activity.title = title || activity.title;
      activity.description = description || activity.description;
      if (activity.description) {
        activity.shortDescription = activity.description.split(".")[0] + ".";
      }

      if (activity.metadata?.terms) {
        activity.metadata.terms = JSON.parse(activity.metadata.terms);
      }

      switch (activity.capabilityType) {
        case "Renter":
          break;

        case "Owner":
          break;
      }

      switch (activity.activityCode) {
        case "MACHINE-AVAILABILITY":
          break;

        case "COI":
          activity.required = false;
          break;
      }
    }

    // return activities;
    // // why did i do this? maybe i can just order by newest first?
    return [...activities.filter(d => d.status != "Cancelled"), ...activities.filter(d => d.status == "Cancelled")];
  }

  getActivityById(id: String) {
    return this.activities.find(d => d.id == id);
  }

  getActivityByCode(activityCode: Activity["activityCode"]): Activity {
    return this.activities.find(d => d.activityCode == activityCode);
  }

  initialActivitiesAreComplete(capability: Activity["capabilityType"]): boolean {
    return (
      this.activities.filter(d => d.capabilityType == capability && d.activityContext == "Initial" && d.required == true && d.status != "Closed").length == 0
    );
  }

  get renterActivities() {
    return this.activities.filter(d => d.capabilityType == "Renter" && d.status != "Dismissed" && d.status != "Closed");
  }

  get ownerActivities() {
    return this.activities.filter(d => d.capabilityType == "Owner" && d.status != "Dismissed" && d.status != "Closed");
  }

  get affiliateActivities() {
    return this.activities.filter(d => d.capabilityType == "Affiliate" && d.status != "Dismissed" && d.status != "Closed");
  }

  uploadFile(activity: Activity, file: File): Observable<Storage> {
    const uploadData = new FormData();
    uploadData.append("file", file);
    const headers = this.getAuth();
    return this.httpClient.post<Storage>(`${this.baseUrl}${this.endpoint}/${activity.id}/attachment?accountId=${activity.accountId}`, uploadData, { headers });
  }
}
